import { SearchOutlined } from '@ant-design/icons';
import { dirhams, FoundRecordCard, LatePayersAlert } from '@horof/shared';
import { Alert, Button, Empty, Input, Segmented, Skeleton } from 'antd';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'urql';
import { type ListFilter, useListState } from './list-state';
import {
  type MemberRow,
  matchesMember,
  PaymentMembersDocument,
  toMemberRows,
} from './members';

const refetchAfterPayments = { additionalTypenames: ['Payment', 'Bill'] };

export function MembersPage() {
  const { search, setSearch, filter, setFilter } = useListState();
  const [{ data, error }, refetch] = useQuery({
    query: PaymentMembersDocument,
    context: refetchAfterPayments,
  });
  const members = useMemo(
    () => toMemberRows(data?.members?.nodes ?? []),
    [data],
  );

  const owing = members.filter((m) => m.unpaidCount > 0);
  const late = members.filter((m) => m.overdue);
  const shown = filter === 'owing' ? owing : filter === 'late' ? late : members;
  const visible = shown.filter((m) => matchesMember(m, search));

  return (
    <div className="page">
      <LatePayersAlert
        actionLabel="عرضهم"
        onOpen={() => {
          setSearch('');
          setFilter('late');
        }}
      />
      <header className="list-header">
        <div className="title-row">
          <Link to="/today" className="today-link">
            مقبوضات اليوم
          </Link>
        </div>
        <Input
          size="large"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="بحث: الاسم، العداد، العنوان"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Segmented<ListFilter>
          block
          size="large"
          value={filter}
          onChange={setFilter}
          options={[
            // «مدينون»: short enough for three tabs on a phone.
            { value: 'owing', label: `مدينون (${owing.length})` },
            { value: 'late', label: `متأخرون (${late.length})` },
            { value: 'all', label: `الكل (${members.length})` },
          ]}
        />
      </header>
      <FoundRecordCard
        text={search}
        href={(f) =>
          f.billId
            ? `/bill/${f.billId}`
            : f.memberId
              ? `/member/${f.memberId}`
              : null
        }
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message="تعذّر تحميل الأعضاء"
          description="تحقّق من الاتصال بالإنترنت."
          action={
            <Button onClick={() => refetch({ requestPolicy: 'network-only' })}>
              أعد المحاولة
            </Button>
          }
        />
      )}
      {!data && !error && <Skeleton active paragraph={{ rows: 8 }} />}

      {data && (
        <ul className="member-list">
          {visible.map((member) => (
            <li key={member.id}>
              <MemberLink member={member} />
            </li>
          ))}
        </ul>
      )}
      {data && visible.length === 0 && (
        <Empty
          description={search ? 'لا يوجد عضو بهذا البحث' : 'لا يوجد أي دَين'}
        />
      )}
    </div>
  );
}

function MemberLink({ member }: { member: MemberRow }) {
  return (
    <Link
      to={`/member/${member.id}`}
      className={`member-row ${member.unpaidCount ? (member.overdue ? 'is-overdue' : 'is-owing') : 'is-clear'}`}
    >
      <span className="member-main">
        <span className="member-name">{member.name}</span>
        <span className="member-sub">
          {member.meterLabels.join('، ') || member.address}
        </span>
      </span>
      <span className="member-owed">
        {member.unpaidCount ? (
          <>
            <span>{dirhams(member.owed)}</span>
            {member.overdue && <span className="overdue-tag">متأخر</span>}
          </>
        ) : (
          <span className="clear-tag">لا شيء</span>
        )}
      </span>
    </Link>
  );
}
